import React, { useState } from 'react'

import Table from '../../components/common/Table'
import { HubItem, HubItemPreview } from '../../modules/infoHub'
import { ArticleDTO } from '../../services/apiClient'
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime'
import { renderBooleanBadge } from '../../utils/displayUtils'
import createArticleActions from '../actionMenus/ArticleActions'
import FileIcons from '../common/FileIcons'
import Modal from '../common/Modal'

interface ArticleTableProps {
    data: ArticleDTO[];
    handleEdit: (row: ArticleDTO) => void;
    handleDelete: (row: ArticleDTO) => void;
    currentPage: number;
    totalPages: number;
    setCurrentPage: (page: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    isLoading: boolean;
}

// The row already carries everything the hub reads except the body, which the preview fetches
// itself — see modules/infoHub.
const toHubItem = (row: ArticleDTO): HubItem => ({
    id: row.id ?? '',
    kind: 'article',
    title: row.title ?? '',
    slug: row.slug ?? '',
    date: row.date ? new Date(row.date as unknown as string).toISOString() : '',
    summary: row.summary ?? '',
    categoryId: row.contentCategoryId ?? '',
    categoryTitle: row.contentCategory?.title ?? '',
    // The admin row holds a FileRecord id; the preview resolves it to a URL itself.
    pictureUrl: '',
    pictureFileRecordId: row.pictureFileRecordId ?? '',
    externalUrl: row.externalUrl ?? '',
    isActive: row.isActive ?? false,
});

const ArticleTable: React.FC<ArticleTableProps> = ({
    data,
    handleEdit,
    handleDelete,
    currentPage,
    totalPages,
    setCurrentPage,
    pageSize,
    setPageSize,
    isLoading,
}) => {
    const [previewRow, setPreviewRow] = useState<ArticleDTO | null>(null);

    return (
        <>
        <Table
            columns={[

                {
                    label: "Picture",
                    key: "pictureFileRecordId",
                    render: (row: ArticleDTO) => (
                        <FileIcons files={[{ id: row.pictureFileRecordId, label: 'Picture' }]} />
                    ),
                },

                {
                    label: "Title",
                    key: "title",
                },

                {
                    label: "Category",
                    key: "contentCategoryId",
                    render: (row: ArticleDTO) => <span>{row.contentCategory?.title ?? '-'}</span>,
                },

                {
                    label: "Date",
                    key: "date",
                    format: (value: Date) => convertToUserTime(value?.toString() ?? '', true),
                },

                {
                    label: "Status",
                    key: "isActive",
                    render: (row: ArticleDTO) => renderBooleanBadge(row.isActive ?? false, 'Active', 'Deactivated'),
                },

                {
                    label: "Added",
                    key: "createdUtc",
                    format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
                },

            ]}
            data={data}
            actions={{
                onEdit: handleEdit,
                extra: (row) =>
                    createArticleActions({
                        onPreview: setPreviewRow,
                        onDelete: handleDelete,
                    })(row),
            }}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
        />

        <Modal
            isOpen={previewRow !== null}
            onClose={() => setPreviewRow(null)}
            title="Info Hub preview"
            widthClassName="max-w-5xl"
        >
            {previewRow && <HubItemPreview item={toHubItem(previewRow)} />}
        </Modal>
        </>
    );
};

export default ArticleTable;
